import { Eye, Pencil, Trash2, ToggleLeft, ToggleRight, Copy, ClipboardList, FileEdit, LayoutTemplate } from "lucide-react";
import type { TableColumn } from "../../common/Table";
import { SimpleTooltip } from "../../common";
import Badge from "../../common/Badge";
import type { PublishedSurvey, DraftSurvey, TemplateItem } from "./types";
import { formatDDMMYYYY } from "../../../utils/timeUtils";
import { surveyTileToken } from "../../../utils/surveyInsights";
import {
  surveyStatusVariant,
  closeLabel,
  questionCountLabel,
  templateUsageLabel,
} from "../../../utils/surveyCreation";

export interface SurveyTableHandlers {
  onView: (id: string) => void;
  onEdit?: (id: string) => void;
  onToggleStatus?: (id: string) => void;
  onDelete: (id: string, title: string) => void;
  isActive?: (record: PublishedSurvey) => boolean;
}

export interface TemplateTableHandlers {
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string, title: string) => void;
  onDelete: (id: string, title: string) => void;
}

const iconBtn =
  "w-8 h-8 grid place-items-center rounded-button transition-colors cursor-pointer border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none";

/** Figma surveys table (dc.html:2807): survey / questions / responses / status / actions. */
export function getSurveyColumns<T extends PublishedSurvey | DraftSurvey>(
  kind: "published" | "draft",
  { onView, onEdit, onToggleStatus, onDelete }: SurveyTableHandlers
): TableColumn<T>[] {
  type Row = PublishedSurvey & Partial<DraftSurvey> & { questionCount?: number };
  const asRow = (record: T): Row => record as unknown as Row;
  return [
    {
      key: "survey",
      title: "Survey",
      label: "Survey",
      required: true,
      render: (_, record) => {
        const r = asRow(record);
        const tile = surveyTileToken(r._id);
        const Icon = kind === "published" ? ClipboardList : FileEdit;
        const sub =
          kind === "published"
            ? `${formatDDMMYYYY((r as PublishedSurvey).startDate) ?? "—"} – ${formatDDMMYYYY((r as PublishedSurvey).endDate) ?? "—"}`
            : `Updated ${formatDDMMYYYY(r.updatedAt) ?? "—"}`;
        return (
          <div className="flex items-center gap-[11px]">
            <div className={`w-[38px] h-[38px] rounded-[11px] grid place-items-center flex-none ${tile.tileBg}`}>
              <Icon className={`w-[19px] h-[19px] ${tile.tileColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <SimpleTooltip label={r.title} side="top">
                <span className="font-bold text-label text-[var(--color-text-primary)] block truncate">
                  {r.title || "Untitled"}
                </span>
              </SimpleTooltip>
              <span className="block text-body-xs text-[var(--color-text-tertiary)] truncate">{sub}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "questions",
      title: "Questions",
      label: "Questions",
      render: (_, record) => {
        const n = asRow(record).questionCount;
        if (typeof n !== "number") {
          return <span className="text-body-sm font-semibold text-[var(--color-text-tertiary)]">—</span>;
        }
        return (
          <span className="text-body-sm font-semibold text-[var(--color-text-strong)] whitespace-nowrap">
            {questionCountLabel(n)}
          </span>
        );
      },
    },
    {
      key: "responses",
      title: "Responses",
      label: "Responses",
      render: (_, record) => {
        const r = asRow(record);
        if (kind !== "published") {
          return (
            <div>
              <div className="text-body-sm text-[var(--color-text-strong)] font-semibold">—</div>
              <div className="text-caption text-[var(--color-text-tertiary)]">Not scheduled</div>
            </div>
          );
        }
        const p = r as PublishedSurvey;
        return (
          <div>
            <div className="text-body-sm text-[var(--color-text-primary)] font-semibold whitespace-nowrap">
              {(p.responseCount ?? 0).toLocaleString()} / {(p.totalResponses ?? 0).toLocaleString()}
            </div>
            <div className="text-caption text-[var(--color-text-tertiary)] whitespace-nowrap">
              {closeLabel(p.endDate, formatDDMMYYYY(p.endDate) ?? "")}
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      render: (_, record) => {
        const r = asRow(record);
        if (kind === "published") {
          const active = (r as PublishedSurvey).isActive;
          return (
            <Badge variant={active ? "green" : "gray"} size="middle">
              {active ? "Published" : "Inactive"}
            </Badge>
          );
        }
        return (
          <Badge variant={surveyStatusVariant("draft")} size="middle">
            Draft
          </Badge>
        );
      },
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      required: true,
      align: "right",
      render: (_, record) => {
        const r = asRow(record);
        return (
        <div className="flex items-center justify-end gap-1">
          <SimpleTooltip label="View" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onView(r._id);
              }}
              aria-label="View"
              className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
            >
              <Eye className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
          {kind === "draft" && onEdit && (
            <SimpleTooltip label="Edit" side="top">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.blur();
                  onEdit(r._id);
                }}
                aria-label="Edit"
                className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
              >
                <Pencil className="w-[15px] h-[15px]" aria-hidden="true" />
              </button>
            </SimpleTooltip>
          )}
          {kind === "published" && onToggleStatus && (
            <SimpleTooltip label="Toggle status" side="top">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.blur();
                  onToggleStatus(r._id);
                }}
                aria-label="Toggle status"
                className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
              >
                {(r as PublishedSurvey).isActive ? (
                  <ToggleRight className="w-[15px] h-[15px]" aria-hidden="true" />
                ) : (
                  <ToggleLeft className="w-[15px] h-[15px]" aria-hidden="true" />
                )}
              </button>
            </SimpleTooltip>
          )}
          <SimpleTooltip label="Delete" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onDelete(r._id, r.title || "Untitled");
              }}
              aria-label="Delete"
              className={`${iconBtn} text-[var(--color-text-tertiary)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error-dark)]`}
            >
              <Trash2 className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        </div>
        );
      },
    },
  ];
}

/** Figma template library table (dc.html:2843): template / questions / usage / actions. */
export function getTemplateColumns({
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: TemplateTableHandlers): TableColumn<TemplateItem>[] {
  return [
    {
      key: "template",
      title: "Template",
      label: "Template",
      required: true,
      render: (_, record) => {
        const tile = surveyTileToken(record._id);
        const sub = [record.category, `Updated ${formatDDMMYYYY(record.updatedAt) ?? "—"}`]
          .filter(Boolean)
          .join(" · ");
        return (
          <div className="flex items-center gap-[11px]">
            <div className={`w-[38px] h-[38px] rounded-[11px] grid place-items-center flex-none ${tile.tileBg}`}>
              <LayoutTemplate className={`w-[19px] h-[19px] ${tile.tileColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <SimpleTooltip label={record.title || record.name} side="top">
                <span className="font-bold text-label text-[var(--color-text-primary)] block truncate">
                  {record.title || record.name}
                </span>
              </SimpleTooltip>
              <span className="block text-body-xs text-[var(--color-text-tertiary)] truncate">{sub}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "questions",
      title: "Questions",
      label: "Questions",
      render: (_, record) => (
        <span className="text-body-sm font-semibold text-[var(--color-text-strong)] whitespace-nowrap">
          {questionCountLabel(record.questionCount ?? 0)}
        </span>
      ),
    },
    {
      key: "usage",
      title: "Usage",
      label: "Usage",
      render: (_, record) => (
        <span className="text-body-sm font-medium text-[var(--color-text-secondary)] whitespace-nowrap">
          {templateUsageLabel(record.usageCount ?? 0)}
        </span>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      required: true,
      align: "right",
      render: (_, record) => (
        <div className="flex items-center justify-end gap-1">
          <SimpleTooltip label="View" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onView(record._id);
              }}
              aria-label="View"
              className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
            >
              <Eye className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
          <SimpleTooltip label="Edit" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onEdit(record._id);
              }}
              aria-label="Edit"
              className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
            >
              <Pencil className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
          <SimpleTooltip label="Duplicate" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onDuplicate(record._id, record.title || record.name);
              }}
              aria-label="Duplicate"
              className={`${iconBtn} text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)]`}
            >
              <Copy className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
          <SimpleTooltip label="Delete" side="top">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                onDelete(record._id, record.title || record.name);
              }}
              aria-label="Delete"
              className={`${iconBtn} text-[var(--color-text-tertiary)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error-dark)]`}
            >
              <Trash2 className="w-[15px] h-[15px]" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        </div>
      ),
    },
  ];
}
